/**
 * Acquisition pipeline widget. Mirrors the design's composition bar + stage
 * chips: a single stacked bar sized by each stage's share of active deals, then
 * a chip per non-terminal stage (highlighted when it holds deals).
 */
import { cn } from "@/lib/utils";

import { DashboardCard } from "./DashboardCard";
import { fmtMoneyShort, type FunnelData } from "./dashboard-metrics";

interface AcquisitionPipelineCardProps {
  funnel: FunnelData;
}

export function AcquisitionPipelineCard({ funnel }: AcquisitionPipelineCardProps) {
  const { stages, totalActive } = funnel;
  const dealWord = totalActive === 1 ? "deal" : "deals";
  const subtitle = `${totalActive} active ${dealWord} across ${stages.length} stages`;

  return (
    <DashboardCard
      title="Acquisition pipeline"
      subtitle={subtitle}
      link={{ label: "Open pipeline", to: "/pipeline" }}
    >
      {totalActive === 0 ? (
        <div className="py-[18px] text-center text-[13px] text-muted-foreground">
          No active deals — head to Find to source your next property.
        </div>
      ) : (
        <>
          <div className="mb-4 flex h-2 overflow-hidden rounded-full bg-muted">
            {stages
              .filter((s) => s.count > 0)
              .map((s) => (
                <div
                  key={s.id}
                  className="h-full transition-[width] duration-300"
                  style={{
                    width: `${(s.count / totalActive) * 100}%`,
                    background: s.color,
                  }}
                />
              ))}
          </div>
          <div className="flex flex-wrap gap-2.5">
            {stages.map((s) => {
              const on = s.count > 0;
              return (
                <div
                  key={s.id}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-[7px] text-[13px]",
                    on ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  <span
                    className="h-2 w-2 flex-shrink-0 rounded-full"
                    style={{ background: s.color, opacity: on ? 1 : 0.4 }}
                  />
                  <span
                    className={cn(
                      "whitespace-nowrap",
                      on ? "font-semibold" : "font-medium",
                    )}
                  >
                    {s.name}
                  </span>
                  <span
                    className={cn(
                      "inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-bold tabular-nums",
                      !on && "bg-border text-muted-foreground",
                    )}
                    style={on ? { background: `${s.color}2b`, color: s.color } : undefined}
                  >
                    {s.count}
                  </span>
                  {on && s.vol > 0 ? (
                    <span className="text-[11.5px] tabular-nums text-muted-foreground/70">
                      · {fmtMoneyShort(s.vol)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </DashboardCard>
  );
}
